import { ActionMoveBase, FiltersMap, withoutIrrelevant } from '@wix/bex-core';
import {
  cairoDragActionUnsuccessfulUpdateInServer,
  cairoDragItem,
  cairoDragItemEndDrop,
  cairoTryAgainClicked,
} from '@wix/bex-core/bi';
import { action } from 'mobx';
import { OptimisticPatch } from '@wix/bex-core/dist/types/state/CollectionOptimisticPatch';
import { CollectionDragAndDropParentState } from './CollectionDragAndDropState';

const withoutDefaults = withoutIrrelevant<
  | 'currentTab'
  | 'currentView'
  | 'currentFilters'
  | 'currentSortOrder'
  | 'listSize'
  | 'filteredListSize'
  | 'initialItems'
>();

export interface CollectionDragAndDropBIParams<T, F extends FiltersMap> {
  state: CollectionDragAndDropParentState<T, F>;
}
export class CollectionDragAndDropBI<T, F extends FiltersMap> {
  readonly state;

  startTime = 0;

  constructor(params: CollectionDragAndDropBIParams<T, F>) {
    this.state = params.state;
  }

  get collection() {
    return this.state.collection;
  }

  get dnd() {
    return this.state._dnd;
  }

  get reportBi() {
    return this.state.reportBi;
  }

  onAttemptDragStart = action((itemId: string) => {
    if (!this.dnd) {
      return () => {};
    }
    const { reportBi, dnd, collection } = this;

    const { _isHandleActive } = dnd;
    const {
      query: { search },
    } = collection;

    const item = collection.getKeyedItem(itemId);

    const startTime = performance.now();

    reportBi(
      withoutDefaults(cairoDragItem)({
        itemId,
        itemIndex: item?.index,
        isFromHandle: _isHandleActive,
        numItemsBefore: collection.bulkSelect?.selectedCount,
        searchQuery: search.value,
        isFromSearch: !search.isEmpty,
      }),
    );

    return action(() => {
      reportBi(
        withoutDefaults(cairoDragItemEndDrop)({
          isFromHandle: _isHandleActive,
          itemId,
          itemIndexBefore: item?.index,
          duration: Math.round(performance.now() - startTime),
          endType: 'drag disabled',
          numItemsBefore: collection.bulkSelect?.selectedCount,
          searchQuery: search.value,
          isFromSearch: !search.isEmpty,
        }),
      );
    });
  });

  onDragStart() {
    if (!this.dnd) {
      return;
    }

    const { reportBi, dnd, collection } = this;
    const { active, _isHandleActive } = dnd;
    const {
      query: { search },
    } = collection;

    if (active == null) {
      return;
    }

    this.startTime = performance.now();

    reportBi(
      withoutDefaults(cairoDragItem)({
        itemId: active.key,
        itemIndex: active.index,
        isFromHandle: _isHandleActive,
        numItemsBefore: collection.bulkSelect?.selectedCount,
        searchQuery: search.value,
        isFromSearch: !search.isEmpty,
        dragAndDropID: dnd._moveId,
      }),
    );
  }

  onDragCancel({ endType }: { endType: 'clicked ESC' | 'drop outside' }) {
    if (!this.dnd) {
      return;
    }
    const { reportBi, dnd, startTime, collection } = this;
    const { active, _isHandleActive } = dnd;
    const {
      query: { search },
    } = collection;

    if (active == null) {
      return;
    }

    reportBi(
      withoutDefaults(cairoDragItemEndDrop)({
        isFromHandle: _isHandleActive,
        itemId: active.key,
        duration: Math.round(performance.now() - startTime),
        endType,
        numItemsBefore: collection.bulkSelect?.selectedCount,
        itemIndexBefore: active.index,
        itemIndexAfter: active.index,
        searchQuery: search.value,
        isFromSearch: !search.isEmpty,
        dragAndDropID: dnd._moveId,
      }),
    );
  }

  onDragEnd(overId: string) {
    if (!this.dnd) {
      return;
    }
    const { reportBi, dnd, startTime, collection } = this;
    const { active, _isHandleActive } = dnd;
    const over = collection.getKeyedItem(overId);

    if (active == null || over == null) {
      return;
    }

    reportBi(
      withoutDefaults(cairoDragItemEndDrop)({
        isFromHandle: _isHandleActive,
        itemId: active.key,
        duration: Math.round(performance.now() - startTime),
        endType: overId === active.key ? 'drop back' : 'drop succeeded',
        numItemsBefore: collection.bulkSelect?.selectedCount,
        itemIndexBefore: active.index,
        itemIndexAfter: over.index,
        dragAndDropID: dnd._moveId,
      }),
    );
  }

  onMoveFailure(params: {
    reason: 'Update failed' | 'Window closed' | 'Left page';
    patches: OptimisticPatch<T>[];
  }) {
    const { reportBi, collection } = this;
    const { reason, patches } = params;
    const errorPatch = patches[0];

    if (!errorPatch.move) {
      return;
    }

    const { from, over, moveId, isFromHandle } = errorPatch.move;

    reportBi(
      withoutDefaults(cairoDragActionUnsuccessfulUpdateInServer)({
        ...errorPatch.collectionSnapshot,
        numItemsBefore: collection.bulkSelect?.selectedCount,
        numOfWaitingUpdates: patches.length,
        timeFromEndDrag: Date.now() - errorPatch.createdAt,
        itemIndexBefore: from.index,
        itemIndexAfter: over.index,
        errorType: reason,
        itemId: from.key,
        dragAndDropID: moveId,
        wasFromHandle: isFromHandle,
      }),
    );
  }

  onMoveCancelled(move: ActionMoveBase<T>) {
    const { reportBi, collection } = this;
    const { optimisticActions } = collection;

    const { from, over, moveId, isFromHandle } = move;

    reportBi(
      withoutDefaults(cairoDragActionUnsuccessfulUpdateInServer)({
        ...(this.state.getCollectionSnapshot?.() ?? {}),
        numItemsBefore: collection.bulkSelect?.selectedCount,
        numOfWaitingUpdates: optimisticActions?.pendingMovePatches.length ?? 0,
        timeFromEndDrag: 0,
        itemIndexBefore: from.index,
        itemIndexAfter: over.index,
        errorType: 'External logic',
        itemId: from.key,
        dragAndDropID: moveId,
        wasFromHandle: isFromHandle,
      }),
    );
  }

  onMoveRetry(params: { patches: OptimisticPatch<T>[] }) {
    const { reportBi, collection } = this;
    const { patches } = params;
    const {
      query: { search },
    } = collection;
    const errorPatch = patches[0];

    reportBi(
      withoutDefaults(cairoTryAgainClicked)({
        ...errorPatch.collectionSnapshot,
        isFromSearch: !search.isEmpty,
        numOfWaitingUpdates: patches.length,
        actionName: 'drag',
      }),
    );
  }
}
